import type { MarketingOutletOption } from '../data/marketingTypes';

export type MappingListFilter = 'selected' | 'inScope' | 'outsideScope';

export const MAPPING_SEARCH_LIMIT = 50;

export function outletSearchHay(outlet: MarketingOutletOption): string {
  return `${outlet.name || ''} ${outlet.city || ''} ${outlet.id}`.toLowerCase();
}

export function outletMatchesQuery(
  outlet: MarketingOutletOption,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return outletSearchHay(outlet).includes(q);
}

export function sortOutlets(
  rows: MarketingOutletOption[],
  scoped: Set<string>,
): MarketingOutletOption[] {
  return [...rows].sort((a, b) => {
    const as = scoped.has(a.id) ? 0 : 1;
    const bs = scoped.has(b.id) ? 0 : 1;
    if (as !== bs) return as - bs;
    return (a.name || a.id).localeCompare(b.name || b.id);
  });
}

export function reviewMappedOutlets(input: {
  outlets: MarketingOutletOption[];
  selectedIds: string[];
  scopedIds: Set<string>;
  filter: MappingListFilter;
  query: string;
}): MarketingOutletOption[] {
  const byId = new Map(input.outlets.map(o => [o.id, o]));
  const selected = input.selectedIds
    .map(id => byId.get(id) || { id, name: id })
    .filter(o => {
      if (input.filter === 'inScope' && !input.scopedIds.has(o.id)) return false;
      if (input.filter === 'outsideScope' && input.scopedIds.has(o.id)) return false;
      return outletMatchesQuery(o, input.query);
    });
  return sortOutlets(selected, input.scopedIds);
}

export function searchOutletsToAdd(input: {
  outlets: MarketingOutletOption[];
  selectedIds: Set<string>;
  query: string;
  limit?: number;
}): { rows: MarketingOutletOption[]; total: number; truncated: boolean } {
  const q = input.query.trim();
  if (!q) return { rows: [], total: 0, truncated: false };
  const matches = input.outlets.filter(
    o => !input.selectedIds.has(o.id) && outletMatchesQuery(o, q),
  );
  const limit = input.limit ?? MAPPING_SEARCH_LIMIT;
  return {
    rows: matches.slice(0, limit),
    total: matches.length,
    truncated: matches.length > limit,
  };
}

export function suggestedOutletsToAdd(input: {
  outlets: MarketingOutletOption[];
  suggestedIds: string[];
  selectedIds: Set<string>;
}): MarketingOutletOption[] {
  const byId = new Map(input.outlets.map(o => [o.id, o]));
  return input.suggestedIds
    .filter(id => !input.selectedIds.has(id))
    .map(id => byId.get(id) || { id, name: id });
}

export function mappingCounts(
  selectedIds: string[],
  scopedIds: Set<string>,
): { selected: number; inScope: number; outsideScope: number } {
  const selected = selectedIds.length;
  const inScope = selectedIds.filter(id => scopedIds.has(id)).length;
  return {
    selected,
    inScope,
    outsideScope: selected - inScope,
  };
}
